import React, { useContext, useEffect, useState } from 'react'

// @mui core components
import { Box, Grid, Typography, Button } from '@mui/material'
import Card from 'atoms/Card'

// Data
import { IntakeContext } from 'context/IntakeContext'
import { selectData } from 'layouts/onboarding'

// Popup
import { toast } from 'react-toastify'

import { renderMuiFormField } from 'utils/forms/renderMuiFormField'
// Debounce
import { useFormDebounce } from 'hooks/useFormDebounce'
import LoadingButton from 'components/LoadingButton'

function Healthcare() {
  const { intakeState, updateIntakeStateAtKey, updateUser } =
    useContext(IntakeContext)

  const { updateIntakeStateAndSubmit, loading } = useFormDebounce()

  // add an onSubmit button and call the context function `updateUser`
  // const onSubmit = () => updateUser()
  const onSubmit = async () => {
    await updateUser()
    toast.info('You have successfully updated your healthcare details', {
      position: 'top-center',
      autoClose: 5000,
      pauseOnFocusLoss: false,
      pauseOnHover: false,
    })
  }
  const isGovernmentEmployee = intakeState?.governmentEmployee
  const [localForm, setLocalForm] = useState({
    healthInsurancePremium: intakeState.healthInsurancePremium || '',
  })
  const handleChange = (field) => (value) => {
    setLocalForm((prev) => ({ ...prev, [field]: value }))
  }

  const healthInsurance = intakeState?.healthInsurance
  const fehbLookup = intakeState?.fehbCode
    ? intakeState.fehbCode.replace('HP ', '').match(/^\d/)
      ? '_' + intakeState.fehbCode.replace('HP ', '')
      : intakeState.fehbCode
    : ''
  const suggestedPremium = selectData.fehb[fehbLookup]

  const dollarCentsFormat = Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  })

  useEffect(() => {
    if (!intakeState.fehbCode) return
    const premium = suggestedPremium?.biweeklyEmp || 0

    if (premium) {
      updateIntakeStateAndSubmit(() =>
        updateIntakeStateAtKey({ healthInsurancePremium: premium })
      )()
      handleChange('healthInsurancePremium')(premium)
    }
  }, [intakeState?.fehbCode, suggestedPremium])

  return (
    <>
      <Box
        pl={3}
        display="flex"
        justifyContent="space-between"
        alignItems="center"
      >
        <Typography display="flex" align="left" variant="body2FunHeader">
          HEALTH INSURANCE
        </Typography>

        <LoadingButton loading={loading} onClick={onSubmit} />
      </Box>
      <Card>
        <Box component="form" p={3}>
          <Grid container columnSpacing={3} rowSpacing={2}>
            {isGovernmentEmployee && (
              <>
                <Grid item xs={12} sm={6}>
                  {renderMuiFormField({
                    autoFocus: false,
                    id: 'healthInsurance',
                    label: 'Which health insurance program are you part of?',
                    placeholder: '',
                    inputType: 'select',
                    value: intakeState.healthInsurance,
                    onChange: updateIntakeStateAndSubmit((healthInsurance) =>
                      updateIntakeStateAtKey({ healthInsurance })
                    ),
                    options: selectData.healthInsurance,
                  })}
                </Grid>

                {healthInsurance === 'fehb' || healthInsurance === 'pshb' ? (
                  <Grid item xs={12} md={8}>
                    {renderMuiFormField({
                      autoFocus: false,
                      id: 'fehbCode',
                      label: `Select Your ${intakeState?.healthInsurance.toUpperCase()} Code`,
                      placeholder: '',
                      inputType: 'select',
                      searchable: true,
                      value: intakeState.fehbCode,
                      onChange: updateIntakeStateAndSubmit((fehbCode) =>
                        updateIntakeStateAtKey({ fehbCode })
                      ),
                      options:
                        healthInsurance === 'fehb'
                          ? selectData.fehbCode.slice(0, 438)
                          : selectData.fehbCode.slice(-234),
                    })}
                    {intakeState?.fehbCode && (
                      <Typography variant="inputNote" component="p">
                        {suggestedPremium?.type
                          ? `Plan Type: ${suggestedPremium?.type}`
                          : ''}
                      </Typography>
                    )}
                  </Grid>
                ) : (
                  <></>
                )}
              </>
            )}

            <Grid item xs={12} sm={6}>
              {renderMuiFormField({
                autoFocus: false,
                id: 'healthInsurancePremium',
                label: 'What is your healthcare premium per pay period?',
                placeholder: `${
                  (healthInsurance === 'fehb' || healthInsurance === 'pshb') &&
                  intakeState?.fehbCode
                    ? '$' + suggestedPremium?.biweeklyEmp
                    : '$275.00'
                }`,
                inputType: 'currencyFloat',
                value: localForm.healthInsurancePremium,
                onChange: (e) => handleChange('healthInsurancePremium')(e),
                onBlur: updateIntakeStateAndSubmit((healthInsurancePremium) =>
                  updateIntakeStateAtKey({ healthInsurancePremium })
                ),
              })}
              {(healthInsurance === 'fehb' || healthInsurance === 'pshb') &&
              intakeState?.fehbCode ? (
                <Typography variant="inputNote" component="p">
                  {suggestedPremium?.biweeklyEmp
                    ? `Suggested Premium Based on Code: $${suggestedPremium?.biweeklyEmp}`
                    : ''}
                </Typography>
              ) : (
                <></>
              )}
            </Grid>
          </Grid>
        </Box>
      </Card>
    </>
  )
}

export default React.memo(Healthcare)
